// src/workers/index.js
// Worker manager.
// Owns singleton Worker instances and provides a Promise-based API
// so callers never deal with postMessage/onmessage directly.
//
// Usage:
//   import { FeedWorker, SyncWorker, GeoWorker } from '@workers';
//   const { posts, counts } = await FeedWorker.sortAndFilter(posts, 'critical');
//   const parishes          = await GeoWorker.nearbyParishes(lat, lon, 25);
//   await SyncWorker.pruneApiCache();
//
// Workers are only created when first called (lazy).
// A worker that errors is destroyed and recreated on next call.
// Falls back to synchronous execution if Workers aren't supported.

// ── Worker base class ─────────────────────────────────────────────
class WorkerBridge {
  constructor(url) {
    this._url     = url;
    this._worker  = null;
    this._pending = new Map();   // requestId → { resolve, reject, timer }
    this._reqId   = 0;
  }

  _getWorker() {
    if (this._worker) return this._worker;
    if (typeof Worker === 'undefined') return null;

    try {
      this._worker = new Worker(this._url, { type: 'module' });

      this._worker.onmessage = (e) => {
        const { requestId, ...payload } = e.data ?? {};
        // Forward worker errors to Sentry bridge on main thread
        if (payload.type === 'NA_WORKER_ERROR') {
          window.dispatchEvent(new CustomEvent('na-worker-error', {
            detail: { workerName: payload.workerName, messageType: null, error: payload.error }
          }));
          return;
        }
        const pending = this._pending.get(requestId);
        if (!pending) return;

        clearTimeout(pending.timer);
        this._pending.delete(requestId);

        if (payload.type?.includes('ERROR')) {
          pending.reject(new Error(payload.error ?? 'Worker error'));
        } else {
          pending.resolve(payload);
        }
      };

      this._worker.onerror = (err) => {
        // On hard error, reject all pending and reset the worker
        for (const [id, pending] of this._pending) {
          clearTimeout(pending.timer);
          pending.reject(new Error(err.message ?? 'Worker crashed'));
          this._pending.delete(id);
        }
        this._worker?.terminate();
        this._worker = null;
      };
    } catch {
      return null;
    }

    return this._worker;
  }

  // Send a message and return a Promise that resolves when the worker replies.
  // timeoutMs: reject if no reply within this time (default 5s).
  _send(message, timeoutMs = 5_000) {
    const worker = this._getWorker();

    // Fallback: synchronous execution if Worker not available
    if (!worker) return this._fallback(message);

    const requestId = ++this._reqId;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pending.delete(requestId);
        reject(new Error(`Worker timeout: ${message.type}`));
      }, timeoutMs);

      this._pending.set(requestId, { resolve, reject, timer });
      worker.postMessage({ ...message, requestId });
    });
  }

  // Subclasses override this to provide synchronous fallbacks.
  _fallback(message) {
    return Promise.reject(new Error(`Worker not available for: ${message.type}`));
  }

  terminate() {
    this._worker?.terminate();
    this._worker = null;
    for (const [, p] of this._pending) p.reject(new Error('Worker terminated'));
    this._pending.clear();
  }
}

// ── Feed Worker ───────────────────────────────────────────────────
class FeedWorkerBridge extends WorkerBridge {
  constructor() {
    super(new URL('./feed.worker.js', import.meta.url));
  }

  async sortAndFilter(posts, filter = 'all') {
    const result = await this._send({ type: 'SORT_AND_FILTER', posts, filter });
    return {
      posts:    result.posts    ?? [],
      allPosts: result.allPosts ?? [],
      counts:   result.counts   ?? {},
    };
  }

  // Synchronous fallback — runs on main thread if Worker not available
  _fallback(message) {
    if (message.type !== 'SORT_AND_FILTER') return super._fallback(message);

    const { posts = [], filter = 'all' } = message;

    const score = (p) => {
      const c = p.confirmCount ?? 0, f = p.flagCount ?? 0;
      return Math.max(0, Math.min(100, 50 + c * 8 - f * 15));
    };

    const visible  = posts.filter((p) => !p.isHidden && (p.flagCount ?? 0) < 5 && (score(p) >= 10 || (p.severity ?? 1) >= 4));
    const sorted   = [...visible].sort((a, b) => (b.severity ?? 1) - (a.severity ?? 1) || new Date(b.createdAt) - new Date(a.createdAt));
    const filtered = filter === 'all' ? sorted : filter === 'critical' ? sorted.filter((p) => (p.severity ?? 1) >= 4) : sorted.filter((p) => p.type === filter);
    const counts   = { total: visible.length, sos: visible.filter((p) => p.type === 'sos').length, critical: visible.filter((p) => (p.severity ?? 1) >= 4).length };

    return Promise.resolve({ posts: filtered, allPosts: sorted, counts, requestId: message.requestId });
  }
}

// ── Sync Worker ───────────────────────────────────────────────────
class SyncWorkerBridge extends WorkerBridge {
  constructor() {
    super(new URL('./sync.worker.js', import.meta.url));
  }

  async pruneDoneItems(maxAgeMs = 24 * 60 * 60_000) {
    const result = await this._send({ type: 'PRUNE_DONE_ITEMS', maxAgeMs }, 30_000);
    return result.pruned ?? 0;
  }

  async pruneApiCache() {
    const result = await this._send({ type: 'PRUNE_API_CACHE' }, 30_000);
    return result.pruned ?? 0;
  }

  async evictOldTiles(maxAgeMs = 30 * 24 * 60 * 60_000) {
    const result = await this._send({ type: 'EVICT_OLD_TILES', maxAgeMs }, 30_000);
    return result.evicted ?? 0;
  }

  async reconcileVersion(clientVersion, serverVersion, opType) {
    const result = await this._send({ type: 'RECONCILE_VERSION', clientVersion, serverVersion, opType });
    return result.strategy ?? 'last_writer_wins';
  }

  async computeBackoff(attempts, baseMs = 5_000, capMs = 10 * 60_000) {
    const result = await this._send({ type: 'COMPUTE_BACKOFF', attempts, baseMs, capMs });
    return result.delayMs ?? Math.min(capMs, baseMs * Math.pow(3, attempts - 1));
  }

  _fallback(message) {
    // Synchronous fallbacks for when Worker isn't available
    if (message.type === 'COMPUTE_BACKOFF') {
      const { attempts = 1, baseMs = 5_000, capMs = 10 * 60_000 } = message;
      return Promise.resolve({ delayMs: Math.min(capMs, baseMs * Math.pow(3, Math.max(0, attempts - 1))) });
    }
    if (message.type === 'RECONCILE_VERSION') {
      const { clientVersion, serverVersion, opType } = message;
      if (serverVersion <= clientVersion) return Promise.resolve({ strategy: 'no_conflict' });
      if (opType === 'confirm_report' || opType === 'flag_report') return Promise.resolve({ strategy: 'merge' });
      return Promise.resolve({ strategy: 'last_writer_wins' });
    }
    // IDB pruning falls back to no-op (non-critical cleanup)
    return Promise.resolve({ pruned: 0, evicted: 0 });
  }
}

// ── Geo Worker ────────────────────────────────────────────────────
class GeoWorkerBridge extends WorkerBridge {
  constructor() {
    super(new URL('./geo.worker.js', import.meta.url));
  }

  async sortByDistance(items, userLat, userLon) {
    if (userLat == null || userLon == null) return items;
    const result = await this._send({ type: 'SORT_BY_DISTANCE', items, userLat, userLon });
    return result.items ?? items;
  }

  async nearbyParishes(userLat, userLon, radiusKm = 25) {
    const result = await this._send({ type: 'NEARBY_PARISHES', userLat, userLon, radiusKm });
    return result.parishes ?? [];
  }

  async generateTileUrls(bounds, zooms = [10, 11, 12, 13]) {
    const result = await this._send({ type: 'GENERATE_TILE_URLS', bounds, zooms }, 10_000);
    return result.urls ?? [];
  }

  async clusterPositions(markers, zoom) {
    const result = await this._send({ type: 'CLUSTER_POSITIONS', markers, zoom });
    return result.clusters ?? markers.map((m) => ({ ...m, count: 1, isSingle: true }));
  }

  _fallback(message) {
    // Synchronous haversine fallback
    if (message.type === 'SORT_BY_DISTANCE') {
      const { items, userLat, userLon } = message;
      if (userLat == null) return Promise.resolve({ items });
      const R = 6371;
      const withDist = items.map((item) => {
        if (!item.lat || !item.lon) return { ...item, distanceMiles: null };
        const dLat = (item.lat - userLat) * Math.PI / 180;
        const dLon = (item.lon - userLon) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2 + Math.cos(userLat*Math.PI/180)*Math.cos(item.lat*Math.PI/180)*Math.sin(dLon/2)**2;
        const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return { ...item, distanceMiles: Math.round(km * 0.621371 * 10) / 10, distanceKm: Math.round(km * 10) / 10 };
      });
      withDist.sort((a, b) => (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999));
      return Promise.resolve({ items: withDist });
    }
    if (message.type === 'NEARBY_PARISHES') return Promise.resolve({ parishes: [] });
    if (message.type === 'GENERATE_TILE_URLS') return Promise.resolve({ urls: [], count: 0 });
    if (message.type === 'CLUSTER_POSITIONS')  return Promise.resolve({ clusters: message.markers?.map((m) => ({ ...m, count: 1, isSingle: true })) ?? [] });
    return super._fallback(message);
  }
}

// ── Singleton instances ───────────────────────────────────────────
// Created lazily on first call — no overhead if a worker is never used.
let _feedWorker = null;
let _syncWorker = null;
let _geoWorker  = null;

export const FeedWorker = {
  get _bridge() { return (_feedWorker ??= new FeedWorkerBridge()); },
  sortAndFilter: (...args) => FeedWorker._bridge.sortAndFilter(...args),
  terminate:     ()        => { _feedWorker?.terminate(); _feedWorker = null; },
};

export const SyncWorker = {
  get _bridge() { return (_syncWorker ??= new SyncWorkerBridge()); },
  pruneDoneItems:    (...args) => SyncWorker._bridge.pruneDoneItems(...args),
  pruneApiCache:     (...args) => SyncWorker._bridge.pruneApiCache(...args),
  evictOldTiles:     (...args) => SyncWorker._bridge.evictOldTiles(...args),
  reconcileVersion:  (...args) => SyncWorker._bridge.reconcileVersion(...args),
  computeBackoff:    (...args) => SyncWorker._bridge.computeBackoff(...args),
  terminate:         ()        => { _syncWorker?.terminate(); _syncWorker = null; },
};

export const GeoWorker = {
  get _bridge() { return (_geoWorker ??= new GeoWorkerBridge()); },
  sortByDistance:   (...args) => GeoWorker._bridge.sortByDistance(...args),
  nearbyParishes:   (...args) => GeoWorker._bridge.nearbyParishes(...args),
  generateTileUrls: (...args) => GeoWorker._bridge.generateTileUrls(...args),
  clusterPositions: (...args) => GeoWorker._bridge.clusterPositions(...args),
  terminate:        ()        => { _geoWorker?.terminate(); _geoWorker = null; },
};
